import React from 'react';
import { Field, Select, WeezHeader, Button, Card, Icon, ToggleCard, ToastContext } from '@weezevent/nacre';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import TicketOptionPartner from '../../models/ticketOptionSttings';
import AnalyticsModel from '../../models/analyticsModel';
import { useForm } from '../../utils/validators';
import { Loader } from 'semantic-ui-react';
import { usePermissions } from '../../user/permissions';

import Stay22Logo from '../../images/Stay22.png';
import RevolugoLogo from '../../images/Revolugo.png';

import './additionalService.scss';

function ExternalLinkContainer({ links }) {
    return (
        <div className="external-links">
            {links &&
                links.length > 0 &&
                links.map((link, index) => {
                    return (
                        <div className="link" key={index}>
                            <a href={link.link} target="_blank" rel="noopener noreferrer">
                                {link.name} <Icon name="external-link" />
                            </a>
                        </div>
                    );
                })}
        </div>
    );
}

const SEND_SMS_DESACTIVATED = '0';
const SEND_SMS_ORDER = '1';
const SEND_SMS_ATTENDEE = '2';
export default function AdditionalService({ event, organization }) {
    const { t } = useTranslation();
    const permissions = usePermissions();
    const toast = React.useContext(ToastContext);

    const [submitting, setSubmitting] = React.useState(false);
    const [socialMedia, setSocialMedia] = React.useState(false);

    const [formSettings, setFormSettings, setFormSettingsObject] = useForm({
        spb_enabled: false,
        accommodation: false,
        accommodation_revolugo: false,
        aff_resend: false,
        showAddToCalendarButtons: false,
        apple_wallet: false,
        sms_ticket_delivery: false,
        protect_group_enabled: false,
        valueDropDown: SEND_SMS_ORDER
    });

    const [analytics, loadingAnalytics] = AnalyticsModel.useApiModel({
        organizationId: organization.pk,
        eventId: event.pk,
        cache: false,
        allow_cache: false,
        onSuccess: data => {
            setSocialMedia(Boolean(Number(data.social_media)));
        }
    });

    const [accommodationsShow, toggleAccommodationsShow] = React.useState(false);
    const [lastToggledAccommodation, setLastToggledAccommodation] = React.useState('');

    function onLoadedSettings(setting) {
        let active = '';
        if (Number(setting.accommodation_revolugo) === 1) {
            active = 'accommodation_revolugo';
        } else if (Number(setting.accommodation) === 1) {
            active = 'accommodation';
        }
        toggleAccommodationsShow(active);
        setLastToggledAccommodation(active);
    }
    const [settings, loading, sync] = TicketOptionPartner.useApiModel(
        {
            organizationId: organization.pk,
            eventId: event.pk,
            onSuccess: data => {
                setFormSettingsObject({
                    spb_enabled: Boolean(Number(data.values.spb_enabled)),
                    accommodation: Boolean(Number(data.values.accommodation)) && !Number(data.values.accommodation_revolugo),
                    accommodation_revolugo: Boolean(Number(data.values.accommodation_revolugo)),
                    aff_resend: Boolean(Number(data.values.aff_resend)),
                    showAddToCalendarButtons: Boolean(Number(data.values.showAddToCalendarButtons)),
                    apple_wallet: Boolean(Number(data.values.apple_wallet)),
                    sms_ticket_delivery: [SEND_SMS_ORDER, SEND_SMS_ATTENDEE].includes(data.values.sms_ticket_delivery),
                    protect_group_enabled: Boolean(Number(data.values.protect_group_enabled)),
                    valueDropDown: data.values.sms_ticket_delivery !== SEND_SMS_DESACTIVATED ? data.values.sms_ticket_delivery : SEND_SMS_ORDER
                });
                onLoadedSettings(data.values);
            }
        },
        [organization, event, setFormSettingsObject]
    );

    const onToggleAccommodations = React.useCallback(
        checked => {
            toggleAccommodationsShow(checked);
            if (!checked) {
                setFormSettingsObject({ ...formSettings, accommodation: false, accommodation_revolugo: false });
            } else if (lastToggledAccommodation) {
                setFormSettings(lastToggledAccommodation, true);
            }
        },
        [formSettings, lastToggledAccommodation, setFormSettings, setFormSettingsObject]
    );

    const onSubmit = React.useCallback(() => {
        setSubmitting(true);
        const _newAnalytics = new AnalyticsModel({
            ...analytics,
            social_media: socialMedia ? '1' : '0'
        });
        const newAnalytics = new TicketOptionPartner({
            ...settings,
            values: {
                ...settings.values,
                spb_enabled: formSettings['spb_enabled'] ? '1' : '0',
                accommodation: formSettings['accommodation'] ? '1' : '0',
                accommodation_revolugo: formSettings['accommodation_revolugo'] ? '1' : '0',
                aff_resend: formSettings['aff_resend'] ? '1' : '0',
                showAddToCalendarButtons: formSettings['showAddToCalendarButtons'] ? '1' : '0',
                apple_wallet: formSettings['apple_wallet'] ? '1' : '0',
                protect_group_enabled: formSettings['protect_group_enabled'] ? '1' : '0',
                sms_ticket_delivery: !formSettings['sms_ticket_delivery'] ? SEND_SMS_DESACTIVATED : formSettings['valueDropDown']
            }
        });
        newAnalytics
            .save({
                organizationId: organization.pk,
                eventId: event.pk
            })
            .then(() => {
                _newAnalytics.save({
                    organizationId: organization.pk,
                    eventId: event.pk
                });
                toast.success(t('ticket.event.integration.success-toast'));
                sync({ allow_cache: false, cache: false });
            })
            .catch(e => {
                console.error(e);
                toast.error(t('ticket.event.integration.failure-toast'));
            })
            .finally(() => {
                setSubmitting(false);
            });
    }, [event, organization, settings, formSettings, sync, socialMedia, toast]);

    const onChangeToggleSms = React.useCallback(
        value => {
            setFormSettingsObject(
                {
                    sms_ticket_delivery: value,
                    valueDropDown: !value ? SEND_SMS_ORDER : formSettings['valueDropDown']
                },
                true
            );
        },
        [formSettings, setFormSettingsObject]
    );

    const onChangeDropdownSms = React.useCallback(
        e => {
            setFormSettings('valueDropDown', e);
        },
        [setFormSettings]
    );

    function accommodationsChoice() {
        function eitherOr(current, checked, a = 'accommodation', b = 'accommodation_revolugo') {
            const other = { [a]: b, [b]: a };
            if (formSettings[other[current]]) {
                setFormSettings(other[current], false);
            }
            setFormSettings(current, checked);
            setLastToggledAccommodation(checked ? current : '');
        }

        return (
            <div className="accommodations">
                <ToggleCard
                    title="Revolugo"
                    name="accommodation_revolugo"
                    logoURL={RevolugoLogo}
                    toggle={formSettings['accommodation_revolugo']}
                    onToggle={checked => eitherOr('accommodation_revolugo', checked)}
                />
                <ToggleCard
                    title="Stay22"
                    name="accommodation"
                    logoURL={Stay22Logo}
                    toggle={formSettings['accommodation']}
                    onToggle={checked => eitherOr('accommodation', checked)}
                />
            </div>
        );
    }

    const optionsBuyerExperience = React.useMemo(() => {
        const flags = ['EO_ADHEREVENT', 'EO_FREE', 'EO_NOBUYER', 'EO_CASHLESS', 'EO_APPR', 'EO_SFS'];
        return [
            {
                toggle: accommodationsShow,
                title: t('ticket.ticketingOption.additionalServices.buyerExperience.activateHotel.title'),
                subtitle: t('ticket.ticketingOption.additionalServices.buyerExperience.activateHotel.description'),
                children: accommodationsChoice(),
                onToggle: checked => onToggleAccommodations(checked)
            },
            {
                toggle: formSettings['aff_resend'],
                title: t('ticket.ticketingOption.additionalServices.buyerExperience.resendOrder.title'),
                subtitle: t('ticket.ticketingOption.additionalServices.buyerExperience.resendOrder.description'),
                onToggle: checked => setFormSettings('aff_resend', checked),
                name: 'aff_resend'
            },
            {
                toggle: socialMedia,
                title: t('ticket.ticketingOption.additionalServices.buyerExperience.socialMedia.title'),
                subtitle: t('ticket.ticketingOption.additionalServices.buyerExperience.socialMedia.description'),
                onToggle: checked => setSocialMedia(checked),
                name: 'social_media'
            },
            {
                toggle: formSettings['showAddToCalendarButtons'],
                title: t('ticket.ticketingOption.additionalServices.buyerExperience.addToCalendar.title'),
                subtitle: t('ticket.ticketingOption.additionalServices.buyerExperience.addToCalendar.description'),
                onToggle: checked => setFormSettings('showAddToCalendarButtons', checked),
                name: 'showAddToCalendarButtons'
            },
            ...(!['Don', 'Cotisation'].includes(event.type) && !event.feature_flags.some(flag => flags.includes(flag)) && settings?.paiement_offline !== '1'
                ? [
                      {
                          toggle: formSettings['protect_group_enabled'],
                          title: t('ticket.ticketingOption.additionalServices.buyerExperience.protectGroup.title'),
                          subtitle: t('ticket.ticketingOption.additionalServices.buyerExperience.protectGroup.description'),
                          onToggle: checked => setFormSettings('protect_group_enabled', checked),
                          name: 'protect_group_enabled'
                      }
                  ]
                : [])
        ];
    }, [event, organization, settings, formSettings, setFormSettings, accommodationsShow, onToggleAccommodations]);

    const optionsTicketFormat = React.useMemo(
        () => [
            {
                toggle: formSettings['apple_wallet'],
                title: t('ticket.ticketingOption.additionalServices.ticketFormat.activeAppelWallette.title'),
                subtitle: t('ticket.ticketingOption.additionalServices.ticketFormat.activeAppelWallette.description'),
                link: [
                    {
                        name: t('ticket.ticketingOption.additionalServices.ticketFormat.activeAppelWallette.link'),
                        link: 'https://support.weezevent.com/fr/ajouter-billet-apple-wallet'
                    }
                ],
                onToggle: checked => setFormSettings('apple_wallet', checked),
                name: 'apple_wallet'
            },
            {
                toggle: formSettings['sms_ticket_delivery'],
                title: t('ticket.ticketingOption.additionalServices.ticketFormat.sendSms.title'),
                subtitle: t('ticket.ticketingOption.additionalServices.ticketFormat.sendSms.description'),
                onToggle: onChangeToggleSms,
                link: [
                    { name: t('ticket.ticketingOption.additionalServices.ticketFormat.sendSms.link'), link: window.location.origin + '/target/O' + organization.id + '/invoicing' },
                    { name: t('ticket.ticketingOption.additionalServices.ticketFormat.sendSms.link2'), link: 'https://sites.weezevent.com/refill/' }
                ],
                name: 'sms_ticket_delivery',
                dropDown: [
                    { text: t('ticket.ticketingOption.additionalServices.ticketFormat.sendSms.dropdown.toOrder'), key: 1, value: '1' },
                    { text: t('ticket.ticketingOption.additionalServices.ticketFormat.sendSms.dropdown.toParticipant'), key: 2, value: '2' }
                ],
                valueDropDown: formSettings['valueDropDown'],
                changeDropDown: onChangeDropdownSms,
                disabled: !formSettings['sms_ticket_delivery'] && !permissions.isAdmin()
            }
        ],
        [organization, formSettings, setFormSettings, onChangeDropdownSms, onChangeToggleSms]
    );

    if (loading || loadingAnalytics) {
        return <Loader active inline="centered" />;
    }

    return (
        <>
            <WeezHeader
                title={t('ticket.ticketingOption.additionalServices.title')}
                rightComponent={[
                    <Button
                        primary
                        icon={<Icon className="cta-save-icon" name="save" margin={false} />}
                        isLoading={submitting}
                        key="primary"
                        label={t('ticket.common.action.save')}
                        onClick={onSubmit}
                    />
                ]}
            />
            <Card>
                <Card.Header>
                    <Card.Title>{t('ticket.ticketingOption.additionalServices.buyerExperience.title')}</Card.Title>
                </Card.Header>
                <Card.Content>
                    {optionsBuyerExperience.map((option, id) => (
                        <ToggleCard key={id} {...option} extraComponent={option.link ? <ExternalLinkContainer links={option.link} /> : null}>
                            {option?.children}
                        </ToggleCard>
                    ))}
                </Card.Content>
            </Card>
            <Card>
                <Card.Header>
                    <Card.Title>{t('ticket.ticketingOption.additionalServices.ticketFormat.title')}</Card.Title>
                </Card.Header>
                <Card.Content>
                    {optionsTicketFormat.map((option, id) => (
                        <ToggleCard key={id} {...option} extraComponent={option.link ? <ExternalLinkContainer links={option.link} /> : null}>
                            {option.dropDown ? (
                                <Field className="toggle-container-dropdown">
                                    <Select onChange={option.changeDropDown} value={option.valueDropDown} options={option.dropDown} />
                                </Field>
                            ) : null}
                        </ToggleCard>
                    ))}
                </Card.Content>
            </Card>
        </>
    );
}

AdditionalService.propTypes = {
    organization: PropTypes.object.isRequired,
    event: PropTypes.object.isRequired
};
